import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { ReactComponent as MeestInfoIcon } from '../../../../../assets/info-tip.svg';
import { ReactComponent as PolonezInfoIcon } from '../../../../../assets/alert-circle.svg';

import { useStore } from '../../../../../stores/store';
import { MarginContainer } from '../../../../../common/base-margin/base-margin.component';
import {
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../../../../common/tabs/tabs.component';
import DeliveryTabTitle from '../../../../parcel-creation/parcel-delivery-options-tabs/parcel-delivery-tab-title.component';
import ParcelDeliveryOption from '../parcel-delivery-option/parcel-delivery-option.component';
import { SelectOption } from '../parcel-delivery-option/parcel-delivery-option.styles';
import Button from '../../../../../common/button/button.component';
import { AppRoute, SearchParam } from '../../../../../constants/route';
import {
  FooterButtons,
  Footer,
  FooterInfoBlock,
  OptionsContainer,
  TabListContainer,
  TabPanelFlexContainer,
  Container,
} from './estimated-delivery-cost.styles';
import {
  generateLocaleRoute,
  generateStartParcelCreationRoute,
} from '../../../../../utils/uri/uri.utils';
import { useStartParcelCreation } from '../../../../../hooks/use-start-parcel-creation.hook';
import CollapsableText from '../../../../../common/collapsable-text/collapsable-text.components';
import { useIsMobile } from '../../../../../hooks/use-is-mobile.hook';
import { isMeest } from '../../../../../utils/client/client.utils';
import { client } from '../../../../../config';
import { DeliveryMethod } from '../../../../../models/delivery-options/common';
import {
  assignDelivetyOptionLabels,
  filterDeliveryOptions,
  sortDeliveryOptions,
} from '../../../../../routes/parcel-delivery/helpers';
import { DeliveryOption } from '../../../../../models/delivery-options/delivery-option';
import { prepareEcommerceItemForCalculator } from '../../../../../services/analytics/helpers';
import { DeliveryOptionRibbonLabelContainer } from '../../../../parcel-creation/parcel-delivery-option/delivery-option-card-label.styles';
import DeliveryOptionCardLabel from '../../../../parcel-creation/parcel-delivery-option/delivery-option-card-label.component';

interface Props {
  isFrameMode?: boolean;
}
const getIcon = () => {
  const icons = {
    meest: <MeestInfoIcon />,
    polonez: <PolonezInfoIcon />,
  };
  return icons[client];
};
const isMeestClient = isMeest(client);

const EstimatedDeliveryCost = ({ isFrameMode }: Props) => {
  const {
    widgetCalculatorStore: {
      countryDeparture,
      countryDestination,
      parcelDimensions,
      deliveryOptions,
      staticTimeStrings,
      cheapestPerMethod,
    },
    parcelCreationStore: { shippingType },
    navStore: { setServicesSidebarVisibility },
    userStore: { user, measures },
    commonStore: { analyticsManager, setIsGuestParcelCreation },
  } = useStore();
  const handleStartParcelCreation = useStartParcelCreation();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const [middleMileMethod, setMiddleMileMethod] = useState<DeliveryMethod>();

  const [middleMileMethods, setMiddleMileMethods] = useState<DeliveryMethod[]>(
    []
  );

  const [deliveryTimes, setDeliveryTimes] = useState<
    Partial<Record<DeliveryMethod, string>>
  >({});

  const [selectedOption, setSelectedOption] = useState<DeliveryOption>();

  const [filteredDeliveryOptions, setFilteredDeliveryOptions] =
    useState<DeliveryOption[]>();

  useEffect(() => {
    const middleMiles = deliveryOptions?.map((item) => item.middle_mile);

    let availableMethods = Array.from(
      new Set(middleMiles?.map((item) => item.delivery_method))
    );

    const order: DeliveryMethod[] = cheapestPerMethod;

    availableMethods = order.filter((method) =>
      availableMethods.includes(method)
    );
    setMiddleMileMethods(availableMethods);

    const deliveryTimesArr: Partial<Record<DeliveryMethod, string>> = {};
    availableMethods?.forEach((x) => {
      const deliveryTime = staticTimeStrings && staticTimeStrings[x];

      if (deliveryTime) {
        deliveryTimesArr[x] = deliveryTime;
      }
    });
    setDeliveryTimes(deliveryTimesArr);
  }, [deliveryOptions, cheapestPerMethod, staticTimeStrings]);

  useEffect(() => {
    if (!deliveryOptions) return;
    assignDelivetyOptionLabels(deliveryOptions);
  }, [deliveryOptions]);

  useEffect(() => {
    if (!deliveryOptions) return;

    const filteredOptions = filterDeliveryOptions(
      deliveryOptions,
      middleMileMethod
    );

    if (!filteredOptions) {
      setFilteredDeliveryOptions(undefined);
      return;
    }

    const sortedOptions = sortDeliveryOptions(filteredOptions, 'recommended');
    setFilteredDeliveryOptions(sortedOptions);
  }, [deliveryOptions, middleMileMethod]);

  useEffect(() => {
    if (!middleMileMethods.length) return;
    setMiddleMileMethod(middleMileMethods.at(0));
  }, [middleMileMethods]);

  const handleCreateOnline = () => {
    setServicesSidebarVisibility(false);
    const urlState = { state: { key: new Date().getTime() } };
    if (shippingType === 'international') {
      const data = encodeURIComponent(
        JSON.stringify({
          // Clear zip + city info because on 1st step user should provide its own data
          countryDeparture: {
            countryCode: countryDeparture?.code,
            zipCode: countryDeparture?.zipCode,
          },
          countryDestination: {
            countryCode: countryDestination?.code,
            zipCode: countryDestination?.zipCode,
          },
          measures,
          parcelDimensions,
        })
      );

      const urlCreateParcelWithData = `${generateStartParcelCreationRoute()}?${SearchParam.DATA}=${data}`;
      let urlDestination = urlCreateParcelWithData;

      analyticsManager.track('add_to_cart', {
        ecommerce: {
          items: [prepareEcommerceItemForCalculator(selectedOption)],
        },
      });

      if (!user) {
        setIsGuestParcelCreation(true);
      }

      if (isFrameMode) {
        const origin = new URL(window.location.href).searchParams.get('origin');
        if (!origin) {
          console.warn("Missing search parameter 'origin' for iFrame mode.");
          return;
        }
        urlDestination = `${urlDestination}&${SearchParam.REQUEST_SOURCE}=iframe`;
        window.parent.postMessage({ url: urlDestination }, origin);
        return;
      }

      handleStartParcelCreation(urlDestination);
    } else {
      navigate(generateLocaleRoute(AppRoute.COMING_SOON), urlState);
    }
  };

  const priceDisclaimerText = t('delivery_cost_info_1');

  const isDisclaimerRequiresCollapsableText = priceDisclaimerText.length > 182;

  const footerTextVisible = !isMobile
    ? priceDisclaimerText.slice(0, 182)
    : priceDisclaimerText.slice(0, 84);

  const footerTextHidden = !isMobile
    ? priceDisclaimerText.slice(182)
    : priceDisclaimerText.slice(84);

  const getMethodIndex = (method?: DeliveryMethod) =>
    middleMileMethods.findIndex((x) => x === method);

  const handleSelectMiddleMile = (index: number) => {
    setSelectedOption(undefined);
    setMiddleMileMethod(middleMileMethods[index]);
  };

  const isSingleMiddleMile = middleMileMethods.length === 1;
  const getTheme = (mileMethod: DeliveryMethod) => {
    if (mileMethod === 'expedited') {
      return 'red';
    }
    if (isMeestClient) {
      return 'blue';
    }
    return 'polonez-calculator';
  };

  if (!filteredDeliveryOptions) return;

  const renderOptions = (deliveryMethod?: DeliveryMethod) => (
    <OptionsContainer
      key={deliveryMethod}
      $isOneOption={filteredDeliveryOptions.length === 1}>
      {filteredDeliveryOptions.map((deliveryOption) => (
        <SelectOption
          key={deliveryOption.uiKey}
          $isSelected={selectedOption === deliveryOption}
          onClick={() => setSelectedOption(deliveryOption)}>
          <DeliveryOptionRibbonLabelContainer>
            {deliveryOption.labels?.map((label, labelIndex) => (
              <DeliveryOptionCardLabel
                type={label}
                order={labelIndex}
                key={label}
              />
            ))}
          </DeliveryOptionRibbonLabelContainer>
          <ParcelDeliveryOption deliveryOption={deliveryOption} />
        </SelectOption>
      ))}
    </OptionsContainer>
  );

  return (
    <Container>
      <Tabs
        size={isMeestClient || isMobile ? 'small' : 'large'}
        gap={isMeestClient && isMobile ? '2px' : '5px'}
        justifyContent={isMeestClient && isMobile ? 'space-between' : 'start'}
        onChange={(index) => handleSelectMiddleMile(index)}
        selectedIndex={getMethodIndex(middleMileMethod)}
        theme={isMeestClient ? 'white' : 'gray'}>
        {middleMileMethods.length > 0 && (
          <TabListContainer
            $isOneTab={isSingleMiddleMile}
            $minWidth={isMeestClient && isMobile ? '51rem' : 'fit-content'}>
            <TabList>
              {middleMileMethods?.map((method, i) => (
                <Tab
                  fullWidth={isMeestClient || !isMobile || isSingleMiddleMile}
                  tabIndex={i.toString()}
                  theme={getTheme(method)}
                  key={method}>
                  <DeliveryTabTitle
                    isSelected={middleMileMethod === method}
                    durationDaysString={deliveryTimes[method]}
                    method={method}
                    isCalculator
                  />
                </Tab>
              ))}
            </TabList>
          </TabListContainer>
        )}
        <MarginContainer
          $mt='24px'
          $mb='24px'>
          <TabPanelFlexContainer>
            {middleMileMethods.length === 0 && renderOptions()}
            {middleMileMethods.map((method) => (
              <TabPanel key={method}>{renderOptions(method)}</TabPanel>
            ))}
          </TabPanelFlexContainer>
        </MarginContainer>
      </Tabs>
      <Footer>
        <FooterInfoBlock
          $vertAlign={
            isDisclaimerRequiresCollapsableText ? 'nornal' : 'center'
          }>
          {getIcon()}
          {isDisclaimerRequiresCollapsableText ? (
            <CollapsableText
              visibleText={footerTextVisible}
              hiddenText={footerTextHidden}
            />
          ) : (
            <p>{priceDisclaimerText}</p>
          )}
        </FooterInfoBlock>
        <FooterButtons>
          <Button
            fullWidth={false}
            size='large'
            appearance={isMeest(client) ? 'brand' : 'polonez-calculator'}
            onClick={handleCreateOnline}>
            {t('create_online')}
          </Button>
        </FooterButtons>
      </Footer>
    </Container>
  );
};

export default observer(EstimatedDeliveryCost);
